import Bugsnag from '@utils/bugsnag';

// Error screens the user actually saw, sent to Bugsnag even though nothing threw. Without this, a screen
// that shows an error while the app keeps running is invisible: the QR check-in showed "Der opstod en fejl
// under checkin" to ~10 students a day at EAAA from 2026-10-05 and Bugsnag saw nothing - it was found
// three days later through a school's report.
//
// - One Bugsnag group per screen (groupingHash), so the dashboard shows a count per error screen.
// - At most once per screen per page load: a student retrying ten times is one event, not ten.
// - `info` by default, which doesn't trigger the new-error e-mails; `warning` for the ones that matter more.

export type ShownErrorScreen =
  | 'qr-checkin-error' // "Der opstod en fejl under checkin"
  | 'qr-checkin-not-registered' // check-in sent, but the student isn't checked in afterwards
  | 'qr-camera-error' // no camera access / camera busy / no camera found
  | 'error-page' // the full-page error (login error, no access, teams-only...)
  | 'session-expired-prompt'; // "sign in again" bar

const reported = new Set<string>();

export function reportShownError(
  screen: ShownErrorScreen,
  details: Record<string, unknown> = {},
  severity: 'info' | 'warning' = 'info',
): void {
  // Separate error pages (e.g. 403 vs 503) are worth a group each.
  const key = screen === 'error-page' && details.code != null ? `${screen}:${details.code}` : screen;
  if (reported.has(key)) return;
  reported.add(key);

  const error = new Error(`Error screen shown: ${key}`);
  error.name = 'ErrorScreenShown';
  try {
    Bugsnag.notify(error, (event) => {
      event.severity = severity;
      event.groupingHash = `ErrorScreenShown:${key}`;
      event.addMetadata('errorScreen', { screen, ...details });
    });
  } catch {
    // Reporting must never break the screen it reports on.
  }
}

/** Test-only: forget which screens were reported. */
export function resetShownErrorReports(): void {
  reported.clear();
}
